// Lo que eligió el vendedor por producto (una que va sola más las acumulables) y el código,
// y cómo viaja en el único PUT como lista de promos.
import { blockedReasonCopy } from './promotionErrorCopy';
import type {
    EvaluatedSalePromotion,
    EvaluateSalePromotionsResponse,
    HoldPromotionInput,
    HoldSalePromotionRequest,
    HoldSalePromotionResponse,
    RetailPromotionsErrorCode,
} from '../api/wizardPromotionsContract';

/** Una promo elegida en un producto. */
export interface PromotionChoice {
    promotionId: string;
    /** Ausente mientras el wizard espera que Promociones la revalide. */
    promotionVersion?: number;
    /** Regalos que eligió el cliente (regalo del equipo o regalo incluido en la promo). */
    giftSkus: string[];
}

/** Promos de un producto: la que va sola (o ninguna) y las que se suman. */
export interface LineSelection {
    solo: PromotionChoice | null;
    stackables: PromotionChoice[];
}

export interface PromotionSelection {
    /** Línea del teléfono; las promos de toda la venta van con ella. */
    anchorLineId: string | null;
    /** Por línea; una línea sin promos puede faltar. */
    byLine: Partial<Record<string, LineSelection>>;
    code: string | null;
}

/** Código que la reevaluación quitó, con el motivo que dio Promociones. */
export interface DroppedCode {
    code: string;
    rejectionCode: RetailPromotionsErrorCode | null;
}

export const EMPTY_LINE: LineSelection = { solo: null, stackables: [] };

export const EMPTY_SELECTION: PromotionSelection = { anchorLineId: null, byLine: {}, code: null };

const byId = (a: { promotionId: string }, b: { promotionId: string }) =>
    a.promotionId.localeCompare(b.promotionId);

/** Las promos de una línea, la que va sola primero. */
export function choicesOn(selection: PromotionSelection, lineId: string): PromotionChoice[] {
    const line = selection.byLine[lineId];
    if (!line) return [];
    return [...(line.solo ? [line.solo] : []), ...line.stackables];
}

/** Ids de todas las promos elegidas en la venta, sin repetir y en orden. */
export function chosenPromotionIds(selection: PromotionSelection): string[] {
    const ids = new Set(
        Object.keys(selection.byLine).flatMap((lineId) =>
            choicesOn(selection, lineId).map((c) => c.promotionId),
        ),
    );
    return [...ids].sort();
}

/** La que va sola en el teléfono, o `null`. */
export function anchorSolo(selection: PromotionSelection): PromotionChoice | null {
    return selection.anchorLineId ? (selection.byLine[selection.anchorLineId]?.solo ?? null) : null;
}

/** Pone la que va sola en la línea (o la quita con `null`). */
export function withSolo(
    selection: PromotionSelection,
    lineId: string,
    choice: PromotionChoice | null,
): PromotionSelection {
    const line = selection.byLine[lineId] ?? EMPTY_LINE;
    return { ...selection, byLine: { ...selection.byLine, [lineId]: { ...line, solo: choice } } };
}

/** Suma o quita una acumulable en la línea. */
export function withStackable(
    selection: PromotionSelection,
    lineId: string,
    choice: PromotionChoice,
    on: boolean,
): PromotionSelection {
    const line = selection.byLine[lineId] ?? EMPTY_LINE;
    const rest = line.stackables.filter((c) => c.promotionId !== choice.promotionId);
    const stackables = on ? [...rest, choice].sort(byId) : rest;
    return { ...selection, byLine: { ...selection.byLine, [lineId]: { ...line, stackables } } };
}

/**
 * Una entrada por promo. En el teléfono no se mandan `lineIds` (las resuelve Promociones, también
 * los accesorios de una que va sola); en los accesorios, las líneas donde se eligió.
 */
export function toHoldRequest(selection: PromotionSelection): HoldSalePromotionRequest {
    const entries = new Map<string, HoldPromotionInput & { onAnchor: boolean }>();
    for (const lineId of Object.keys(selection.byLine).sort()) {
        for (const choice of choicesOn(selection, lineId)) {
            if (choice.promotionVersion === undefined) continue;
            const entry = entries.get(choice.promotionId) ?? {
                promotionId: choice.promotionId,
                promotionVersion: choice.promotionVersion,
                lineIds: [],
                giftSkus: [],
                onAnchor: false,
            };
            if (lineId === selection.anchorLineId) entry.onAnchor = true;
            else entry.lineIds = [...(entry.lineIds ?? []), lineId];
            entry.giftSkus = [...new Set([...(entry.giftSkus ?? []), ...choice.giftSkus])];
            entries.set(choice.promotionId, entry);
        }
    }
    const promotions = [...entries.values()].sort(byId).map(({ onAnchor, ...entry }) => ({
        promotionId: entry.promotionId,
        promotionVersion: entry.promotionVersion,
        ...(!onAnchor && entry.lineIds && entry.lineIds.length > 0 && { lineIds: entry.lineIds }),
        ...(entry.giftSkus && entry.giftSkus.length > 0 && { giftSkus: entry.giftSkus }),
    }));
    return { promotions, discountCode: selection.code };
}

/** Sin las líneas que ya no están en la venta; la ancla se queda. */
export function withLiveLines(
    selection: PromotionSelection,
    liveLineIds: readonly string[],
): PromotionSelection {
    const live = new Set([
        ...liveLineIds,
        ...(selection.anchorLineId ? [selection.anchorLineId] : []),
    ]);
    const byLine = Object.fromEntries(
        Object.entries(selection.byLine).filter(([lineId]) => live.has(lineId)),
    );
    return { ...selection, byLine };
}

/** La selección sin esa línea ni el código; sin la línea no queda ancla. */
export function withoutAnchorLine(
    selection: PromotionSelection,
    lineId: string,
): PromotionSelection {
    const byLine = Object.fromEntries(
        Object.entries(selection.byLine).filter(([id]) => id !== lineId),
    );
    return { anchorLineId: null, byLine, code: null };
}

export function isEmptySelection(selection: PromotionSelection): boolean {
    return chosenPromotionIds(selection).length === 0 && !selection.code;
}

/**
 * Lo que quedó apartado según Promociones; sin líneas, la promo va con el teléfono. La «sola» de
 * cada línea es la que se pidió así: otra que no se suma (p. ej. la de accesorios) no la desplaza.
 */
export function selectionFromHeld(
    held: HoldSalePromotionResponse,
    anchorLineId: string | null,
    requested?: PromotionSelection,
): PromotionSelection {
    let selection: PromotionSelection = { anchorLineId, byLine: {}, code: held.discountCode };
    const askedAsStackable = (lineId: string, promotionId: string): boolean => {
        const asked = requested?.byLine[lineId];
        if (asked?.stackables.some((c) => c.promotionId === promotionId)) return true;
        return asked?.solo != null && asked.solo.promotionId !== promotionId;
    };
    for (const promo of [...held.promotions].sort(byId)) {
        const choice = {
            promotionId: promo.promotionId,
            promotionVersion: promo.promotionVersion,
            giftSkus: promo.giftSkus,
        };
        const lines = promo.lineIds.length > 0 ? promo.lineIds : anchorLineId ? [anchorLineId] : [];
        for (const lineId of lines) {
            selection =
                promo.stackable || askedAsStackable(lineId, promo.promotionId)
                    ? withStackable(selection, lineId, choice, true)
                    : withSolo(selection, lineId, choice);
        }
    }
    return selection;
}

function selectable(
    evaluation: EvaluateSalePromotionsResponse,
    promotionId: string,
): EvaluatedSalePromotion | undefined {
    return evaluation.promotions.find((p) => p.promotionId === promotionId && !p.blockedBy);
}

function inStockSkus(promo: EvaluatedSalePromotion, skus: string[]): string[] {
    return skus.filter((sku) => promo.giftOptions.some((o) => o.sku === sku && o.inStock));
}

/** La elección con la versión vigente; `null` si ya no aplica o su regalo se quedó sin existencia. */
function reconcileChoice(
    choice: PromotionChoice,
    evaluation: EvaluateSalePromotionsResponse,
): PromotionChoice | null {
    const promo = selectable(evaluation, choice.promotionId);
    if (!promo) return null;
    const giftSkus = inStockSkus(promo, choice.giftSkus);
    if (promo.kind === 'GIFT' && choice.giftSkus.length > 0 && giftSkus.length === 0) return null;
    return { promotionId: promo.promotionId, promotionVersion: promo.version, giftSkus };
}

/**
 * Acomoda cada elección según la evaluación: la que no se suma va como «la que va sola» del
 * producto (si llegan dos, se queda la primera) y las demás como acumulables.
 */
export function normalizeKinds(
    selection: PromotionSelection,
    evaluation: EvaluateSalePromotionsResponse,
): PromotionSelection {
    const stackable = new Map(evaluation.promotions.map((p) => [p.promotionId, p.stackable]));
    const offPhone = new Set(
        evaluation.promotions.filter((p) => !p.anchorBound).map((p) => p.promotionId),
    );
    const byLine: Partial<Record<string, LineSelection>> = {};
    for (const lineId of Object.keys(selection.byLine)) {
        const line: LineSelection = { solo: null, stackables: [] };
        const current = selection.byLine[lineId];
        const onAnchor = lineId === selection.anchorLineId;
        for (const choice of choicesOn(selection, lineId)) {
            // Una que la evaluación no lista se queda donde estaba (va sola si así se apartó).
            // Una de accesorios anotada en el teléfono no le quita su lugar a la que va sola.
            const known = stackable.get(choice.promotionId);
            const goesAlone =
                onAnchor && offPhone.has(choice.promotionId)
                    ? false
                    : known === undefined
                      ? current?.solo?.promotionId === choice.promotionId
                      : !known;
            if (goesAlone) {
                line.solo ??= choice;
            } else if (!line.stackables.some((c) => c.promotionId === choice.promotionId)) {
                line.stackables.push(choice);
            }
        }
        line.stackables.sort(byId);
        byLine[lineId] = line;
    }
    return { ...selection, byLine };
}

/**
 * Pasa la selección al teléfono nuevo: lo del anterior se mueve al nuevo, se sueltan líneas que
 * ya no están y se suma lo que la sesión tiene apartado y la memoria no conocía.
 */
export function rebaseOnAnchor(
    selection: PromotionSelection,
    anchorLineId: string,
    liveLineIds: readonly string[],
    session: PromotionSelection,
): PromotionSelection {
    const live = new Set([anchorLineId, ...liveLineIds]);
    let next: PromotionSelection = { ...selection, anchorLineId, byLine: {} };
    const place = (lineId: string, choice: PromotionChoice, solo: boolean) => {
        next = solo
            ? withSolo(next, lineId, next.byLine[lineId]?.solo ?? choice)
            : withStackable(next, lineId, choice, true);
    };
    for (const [lineId, line] of Object.entries(selection.byLine)) {
        if (!line) continue;
        const target = lineId === selection.anchorLineId ? anchorLineId : lineId;
        if (!live.has(target)) continue;
        if (line.solo) place(target, line.solo, true);
        for (const choice of line.stackables) place(target, choice, false);
    }
    const known = new Set(chosenPromotionIds(next));
    for (const [lineId, line] of Object.entries(session.byLine)) {
        for (const choice of line ? [...(line.solo ? [line.solo] : []), ...line.stackables] : []) {
            if (!known.has(choice.promotionId) && live.has(lineId)) place(lineId, choice, false);
        }
    }
    return next;
}

/** Lo que sigue aplicando tras reevaluar (REQ-13, derivar), producto por producto. */
export function reconcileSelection(
    selection: PromotionSelection,
    evaluation: EvaluateSalePromotionsResponse,
): PromotionSelection {
    const byLine: Partial<Record<string, LineSelection>> = {};
    for (const [lineId, line] of Object.entries(normalizeKinds(selection, evaluation).byLine)) {
        if (!line) continue;
        byLine[lineId] = {
            solo: line.solo ? reconcileChoice(line.solo, evaluation) : null,
            stackables: line.stackables
                .map((c) => reconcileChoice(c, evaluation))
                .filter((c): c is PromotionChoice => c !== null),
        };
    }
    return {
        anchorLineId: selection.anchorLineId,
        byLine,
        code: selection.code && evaluation.discountCode?.applicable ? selection.code : null,
    };
}

/** El código que `reconcileSelection` quita y por qué, o `null` si no quitó ninguno. */
export function droppedCodeOf(
    selection: PromotionSelection,
    evaluation: EvaluateSalePromotionsResponse,
): DroppedCode | null {
    const code = selection.code;
    const evaluated = evaluation.discountCode;
    if (!code || evaluated?.applicable) return null;
    return { code, rejectionCode: evaluated?.rejectionCode ?? null };
}

/** Promos elegidas que Promociones ya no lista: se pausaron o vencieron. */
export function stalePromotionIds(
    selection: PromotionSelection,
    evaluation: EvaluateSalePromotionsResponse,
): string[] {
    const listed = new Set(evaluation.promotions.map((p) => p.promotionId));
    return chosenPromotionIds(selection).filter((id) => !listed.has(id));
}

/** Promo apartada que el reapply suelta aunque Promociones la siga listando, con su motivo. */
export interface BlockedDrop {
    promotionId: string;
    reason: string;
}

/** Elegidas que siguen listadas pero ya no se pueden elegir (bloqueo o regalo sin existencia). */
export function blockedDrops(
    selection: PromotionSelection,
    evaluation: EvaluateSalePromotionsResponse,
): BlockedDrop[] {
    const drops = new Map<string, BlockedDrop>();
    for (const lineId of Object.keys(selection.byLine)) {
        for (const choice of choicesOn(selection, lineId)) {
            const promo = evaluation.promotions.find((p) => p.promotionId === choice.promotionId);
            if (!promo || drops.has(promo.promotionId)) continue;
            if (promo.blockedBy) {
                drops.set(promo.promotionId, {
                    promotionId: promo.promotionId,
                    reason: blockedReasonCopy(promo.blockedBy),
                });
            } else if (
                promo.kind === 'GIFT' &&
                choice.giftSkus.length > 0 &&
                inStockSkus(promo, choice.giftSkus).length === 0
            ) {
                drops.set(promo.promotionId, {
                    promotionId: promo.promotionId,
                    reason: 'El regalo se quedó sin existencia',
                });
            }
        }
    }
    return [...drops.values()];
}
